import { useCallback, useEffect } from 'react';
import {
	Typography,
	Form,
	FormInstance,
	Button,
	Alert,
	Grid,
	Divider,
	Checkbox,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useProposerRecommendationPage } from './useProposerRecommendationPage';
import { InterviewResult } from '../InterviewReportCard/InterviewResult';
import { RecommendationComponent } from '../RecommendationComponent';
import { PredefinedReasonCategory } from '../../../shared/Constants';
import { useAuth } from '../../../hooks/useAuth';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface ProposerRecommendationCollectionPageProps {
	form: FormInstance;
}

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description: string;
}) => {
	return (
		<div className="flex flex-col mb-[8px]">
			<Text className="font-sans font-bold text-lg text-primary-blue">
				{heading}
			</Text>
			<Text className="mb-[8px]" type="secondary">
				{description}
			</Text>
		</div>
	);
};

export const ProposerRecommendationCollectionPage = ({
	form,
}: ProposerRecommendationCollectionPageProps) => {
	const { t } = useTranslation();
	const { role } = useAuth();
	const { application } = useProposerRecommendationPage({});
	const proposerRecommendation = application?.proposerRecommendation;
	const isProposerRecommendationDone =
		proposerRecommendation && Object.keys(proposerRecommendation).length > 0;

	const isAdminOrApprover =
		role === UserRole.APPROVER || role === UserRole.ADMIN;

	useEffect(() => {
		if (
			proposerRecommendation &&
			Object.keys(proposerRecommendation).length > 0
		) {
			form.setFieldsValue({ videoCallCheckbox: true });
		}
	}, [proposerRecommendation, form]);

	const handleVideoCallInterviewCheckboxChange = useCallback(
		(checked: boolean) => {
			form.setFieldsValue({
				videoCallCheckbox: checked,
			});
		},
		[form]
	);

	return (
		<div className="flex flex-col h-full min-h-0 max-w-[600px]">
			<Form
				name="basic"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				style={{ maxWidth: 600 }}
				initialValues={{ remember: true }}
				autoComplete="off"
				form={form}
			>
				<SectionHeader
					heading={t('provideProposerFinalRecommendation')}
					description={t('pleaseProvideBasedOnAllInterviews')}
				/>
				<div className="flex flex-row items-start">
					<div className="flex flex-col w-full">
						<RecommendationComponent
							commentsMandatory
							commentsLabel="yourCommentsToSupportAboveRecommendation"
							commentsPlaceholder="commentsAreMandatory"
							form={form}
							category={PredefinedReasonCategory.PROPOSER_REJECT}
							displayComments
							disabled={isProposerRecommendationDone || isAdminOrApprover}
						/>
						<Form.Item name="videoCallCheckbox" valuePropName="checked">
							<Checkbox
								checked={isProposerRecommendationDone}
								onChange={(e) =>
									handleVideoCallInterviewCheckboxChange(e.target.checked)
								}
								disabled={isAdminOrApprover}
							>
								{t('iHaveSuccessfullyCompletedInterviewViaVideoCall')}
							</Checkbox>
						</Form.Item>
					</div>
				</div>
			</Form>
		</div>
	);
};

export const ProposerRecommendationCollectionPageContainer = () => {
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { role } = useAuth();
	const isAdminOrApprover =
		role === UserRole.APPROVER || role === UserRole.ADMIN;
	const {
		form,
		onSubmitClick,
		onCancelClick,
		submitButtonDisabled,
		application,
	} = useProposerRecommendationPage({});

	return (
		<div className="flex justify-center bg-bground-blue">
			<div
				className={`flex flex-col h-full min-h-0 rounded px-5 py-4 ${
					isMobile ? 'w-full' : 'w-2/5 border border-solid border-gray-200'
				}`}
			>
				<Text className="flex-grow font-sans font-semibold text-lg text-secondary-black mt-4 mb-0">
					{t('proposerRecommendation')}
				</Text>
				<Divider className="mt-2 mb-4 border border-solid border-gray-200" />
				<InterviewResult application={application} />
				<Divider className="border border-solid border-gray-200" />
				<ProposerRecommendationCollectionPage form={form} />
				<div className="flex flex-row p-[8px] justify-center">
					<Button onClick={onCancelClick}>{t('cancel')}</Button>
					<Button
						onClick={onSubmitClick}
						type="primary"
						disabled={submitButtonDisabled || isAdminOrApprover}
						className="ml-[16px]"
					>
						{t('submit')}
					</Button>
				</div>
				{application?.isApprover && (
					<Alert
						showIcon
						message={t('onlyProposersAreAllowedToSubmitFeedback')}
						type="warning"
					/>
				)}
			</div>
		</div>
	);
};
